<script setup>
// core
import {ref} from "vue";
import {useRouter} from "vue-router";

const router = useRouter()

const isPlaying = ref(false)

const handleAction = async () => {
    await router.push({name: 'TelephonyActivation'})
}
</script>

<template>
    <div class="hiw">
        <div class="hiw__item hiw__item--video" :class="{ 'is-playing': isPlaying }">
            <!-- !TODO: an empty player until the tutorial video is recorded -->
            <video v-if="isPlaying" class="hiw__video" controls autoplay playsinline></video>

            <template v-else>
                <button
                    type="button"
                    class="hiw__play"
                    :title="$t('telephony.settings.how_it_works.watch')"
                    @click="isPlaying = true"
                >
                    <i class="bi bi-play-fill"></i>
                </button>

                <p class="hiw__videoTitle">{{ $t('telephony.settings.how_it_works.video_title') }}</p>
                <p class="hiw__videoNote">{{ $t('telephony.settings.how_it_works.video_note') }}</p>
            </template>
        </div>

        <div class="hiw__item hiw__item--info">
            <span class="hiw__flag">{{ $t('telephony.settings.how_it_works.flag') }}</span>

            <div class="hiw__block">
                <p class="hiw__title">{{ $t('telephony.settings.how_it_works.title') }}</p>

                <p class="hiw__text">{{ $t('telephony.settings.how_it_works.lead') }}</p>

                <p class="hiw__text">
                    <b>{{ $t('telephony.settings.how_it_works.stats_bold') }}</b>
                    {{ $t('telephony.settings.how_it_works.stats_text') }}
                </p>

                <p class="hiw__text">{{ $t('telephony.settings.how_it_works.no_setup') }}</p>

                <p class="hiw__text --bold">{{ $t('telephony.settings.how_it_works.steps_title') }}</p>

                <ol class="hiw__steps">
                    <li v-for="(step, i) in $tm('telephony.settings.how_it_works.steps')" :key="i">
                        <span class="hiw__num">{{ i + 1 }}</span>
                        <span class="--bold">{{ step }}</span>
                    </li>
                </ol>

                <p class="hiw__text">{{ $t('telephony.settings.how_it_works.beta_note') }}</p>
            </div>

            <button type="button" class="hiw__connect" @click="handleAction">
                <i class="bi bi-telephone-plus"></i>
                {{ $t('telephony.settings.how_it_works.connect') }}
            </button>
        </div>
    </div>
</template>

<style scoped lang="scss">
.hiw {
    display: flex;
    align-items: stretch;
    gap: 30px;

    flex-wrap: wrap;

    &__block {
        display: flex;
        flex-direction: column;
        gap: 12px;
    }

    &__title {
        margin: 0;
        font-family: 'Gilroy', sans-serif;
        font-size: 18px;
        font-weight: 700;
        line-height: 25px;
        color: var(--text-color);
    }

    &__steps {
        list-style: none;
        margin: 0;
        padding: 0;

        display: flex;
        flex-direction: column;
        gap: 10px;

        li {
            display: flex;
            align-items: flex-start;
            gap: 12px;

            font-family: 'Gilroy', sans-serif;
            font-size: 16px;
            line-height: 25px;
            color: var(--text-third-color);
        }

        .--bold {
            font-weight: 700;
            color: var(--text-color);
        }
    }

    &__num {
        flex: 0 0 auto;
        width: 26px;
        height: 26px;
        margin-top: 1px;

        display: inline-flex;
        align-items: center;
        justify-content: center;

        border-radius: 7px;
        background-color: #cdcaf3;
        color: var(--general-color);
        font-family: 'Gilroy', sans-serif;
        font-size: 13px;
        font-weight: 700;
    }

    &__item {
        flex: 1 1 420px;
        min-width: 0;
        padding: 20px 30px;
        border-radius: 11px;

        &--video {
            padding: 0;
            border: 1px solid #cdcaf3;
            min-height: 356px;
            overflow: hidden;

            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            gap: 6px;
        }

        &--info {
            position: relative;
            background: #F4F3FC;
            display: flex;
            flex-direction: column;
            justify-content: space-between;
            gap: 20px;
        }
    }

    &__play {
        width: 92px;
        height: 92px;
        min-width: 0;
        max-width: none;
        margin-bottom: 14px;
        padding: 0;

        display: flex;
        align-items: center;
        justify-content: center;

        border: none;
        border-radius: 50%;
        background: var(--block-color);
        color: var(--general-color);
        cursor: pointer;
        transition: 0.25s;

        i {
            font-size: 34px;
            line-height: 1;
            margin-left: 3px;
        }

        &:hover {
            background: var(--general-color);
            color: #ffffff;
        }
    }

    &__videoTitle {
        margin: 0;
        font-family: 'Gilroy', sans-serif;
        font-size: 16px;
        font-weight: 700;
        line-height: 25px;
        text-align: center;
        color: var(--text-color);
    }

    &__videoNote {
        margin: 0;
        font-family: 'Gilroy', sans-serif;
        font-size: 14px;
        font-weight: 500;
        line-height: 20px;
        text-align: center;
        color: var(--text-third-color);
    }

    &__video {
        width: 100%;
        height: 100%;
        display: block;
        background: #000000;
        object-fit: cover;
    }

    &__flag {
        position: absolute;
        top: -10px;
        right: 14px;
        z-index: 1;

        padding: 7px 14px 7px 22px;
        border-radius: 0 7px 7px 0;
        background: var(--ribbon-primary-color);
        clip-path: polygon(10px 0, 100% 0, 100% 100%, 10px 100%, 0 50%);

        font-size: 0.8rem;
        font-weight: bold;
        line-height: 1;
        text-transform: uppercase;
        white-space: nowrap;
        color: black;

        &::before {
            content: "";
            position: absolute;
            top: 50%;
            left: 13px;
            width: 5px;
            height: 5px;
            border-radius: 50%;
            background: #ffffff;
            transform: translateY(-50%);
        }
    }

    &__connect {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 10px;

        height: 43px;
        min-width: 0;
        max-width: none;
        padding: 0 16px;

        border: 1px solid #7A70DF;
        border-radius: 9px;
        background: #ffffff;
        color: #7A70DF;
        cursor: pointer;
        transition: 0.25s;

        font-family: 'Gilroy', sans-serif;
        font-size: 15px;
        font-weight: 600;
        line-height: 17px;
        letter-spacing: 0.02em;
        text-transform: uppercase;
        white-space: nowrap;

        i {
            font-size: 16px;
        }

        &:hover {
            background: #7A70DF;
            color: #ffffff;
        }
    }

    &__text {
        color: var(--text-third-color);
        font-family: 'Gilroy', sans-serif;
        font-size: 16px;
        font-style: normal;
        font-weight: 400;
        line-height: 25px;

        b,
        &.--bold {
            font-weight: 700;
            color: var(--text-color);
        }
    }
}
</style>
